import { useMemo } from 'react'

import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { type EntityDescriptor, type EntitySetVerdict, checkEntitySetAccess } from '@/utils/access-control/perEntity'

export type EntitySetAccessResult =
	| { allowed: true; verdict: EntitySetVerdict }
	| { allowed: false; reason: 'no-entity-permission'; verdict: EntitySetVerdict }

/**
 * Per-entity-set RBAC primitive — the bulk counterpart to `useEntityAccess`.
 *
 * Reads the current platform user's permissions and runs `checkEntitySetAccess`.
 * For `'owners'` descriptors, the current user's email and role name are
 * auto-injected so callers only have to supply `owners`.
 *
 * Default gating semantics: a bulk action is allowed iff every entity in the
 * set passes its individual check (`verdict.allAllowed`). Callers that need
 * "any" or "count" semantics can read the verdict directly.
 *
 * Returns `{ allowed: true, verdict }` when no `entities` is supplied or the
 * set is empty — keeps the call site simple for components that conditionally
 * compute a set.
 */
export function useEntitySetAccess(entities?: EntityDescriptor[]): EntitySetAccessResult {
	const user = useAuthStore(s => s.user)

	return useMemo((): EntitySetAccessResult => {
		const list = entities ?? []
		if (list.length === 0) {
			return {
				allowed: true,
				verdict: { allAllowed: true, allowedCount: 0, deniedCount: 0, total: 0 },
			}
		}

		const platformUser = user && isPlatformUser(user) ? user : undefined
		const permissions = platformUser?.role?.permissions
		const email = user?.email
		const roleName = platformUser?.role?.name

		// Auto-inject actor identities for any 'owners' descriptors that omit them.
		const resolved = list.map(entity =>
			entity.kind === 'owners'
				? { ...entity, email: entity.email ?? email, roleName: entity.roleName ?? roleName }
				: entity,
		)

		const verdict = checkEntitySetAccess(permissions, resolved)
		return verdict.allAllowed ? { allowed: true, verdict } : { allowed: false, reason: 'no-entity-permission', verdict }
	}, [entities, user])
}
